/**
 * ReaderPageRenderer - Canonical Page Builder Renderer for Reader
 * 
 * Route: /read/:contentType/:contentId/:unitId
 * 
 * Phase 3-C: Now fetches reader config from Page Builder API.
 * The reader_shell section settings control ReaderShell behavior.
 * 
 * Pipeline: CanonicalRouteProvider → registry → ReaderPageRenderer → ReaderShell
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle, BookOpen } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { useLegacyRendererDebug } from '../../CanonicalRouteProvider';
import AdminDebugPill from '../AdminDebugPill';

import { 
  ReaderContextProvider,
  useReaderContext,
  READER_MODES, 
  DEFAULT_MODE,
  parseModeFromQuery,
} from './hooks/useReaderContext';
import { useComicEpisodeContent } from './hooks/useComicEpisodeContent';
import { useNovelChapterContent } from './hooks/useNovelChapterContent';

import ReaderShell from './ReaderShell';
import VerticalMode from './modes/VerticalMode';
import HorizontalMode from './modes/HorizontalMode';
import PageMode from './modes/PageMode';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MODE_COMPONENTS = {
  [READER_MODES.VERTICAL]: VerticalMode,
  [READER_MODES.HORIZONTAL]: HorizontalMode,
  [READER_MODES.PAGE]: PageMode,
};

// Default reader shell settings (matches backend DEFAULT_PAGE_BUILDER_PAGES.reader)
const DEFAULT_SHELL_SETTINGS = {
  showProgressBar: true,
  showModeToggle: true,
  immersiveByDefault: false,
  allowModeSwitching: true,
  enableKeyboardNav: true,
  enableSwipeNav: true,
  showChapterNav: true,
};

const DEFAULT_FOOTER_SETTINGS = {
  showSeriesLink: true,
  showCreatorLink: true,
  showShareButtons: true,
  showComments: false,
};

const ReaderLoading = () => (
  <div className="min-h-screen bg-black flex items-center justify-center" data-testid="reader-loading">
    <div className="text-center">
      <Loader2 className="w-10 h-10 text-orange-500 animate-spin mx-auto mb-4" />
      <p className="text-zinc-400">Loading reader...</p>
    </div>
  </div>
);

const ReaderError = ({ message, onRetry }) => (
  <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="reader-error">
    <div className="max-w-md text-center">
      <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-white mb-2">Failed to Load Reader</h1>
      <p className="text-zinc-400 mb-6">{message || 'Something went wrong.'}</p>
      {onRetry && (
        <Button onClick={onRetry} className="bg-orange-500 hover:bg-orange-600" data-testid="reader-retry-btn">
          Try Again
        </Button>
      )}
    </div>
  </div>
);

const ReaderInvalidParams = ({ message }) => (
  <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="reader-invalid">
    <div className="max-w-md text-center">
      <BookOpen className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-white mb-2">Invalid Reader URL</h1>
      <p className="text-zinc-400 mb-6">{message || 'Please check the URL and try again.'}</p>
      <Button onClick={() => window.history.back()} variant="outline" className="border-zinc-700" data-testid="reader-go-back-btn">
        Go Back
      </Button>
    </div>
  </div>
);

/**
 * Hook to fetch reader page config from Page Builder API
 */
const useReaderPageConfig = (version = 'published') => {
  const [shellSettings, setShellSettings] = useState(DEFAULT_SHELL_SETTINGS);
  const [footerSettings, setFooterSettings] = useState(DEFAULT_FOOTER_SETTINGS);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const versionParam = version === 'draft' ? '?version=draft' : '';
        const res = await fetch(`${API_URL}/api/page-builder/render/reader${versionParam}`);
        if (!res.ok) {
          console.warn('[ReaderPageRenderer] Failed to fetch reader config, using defaults');
          setConfigLoaded(true);
          return;
        }
        const data = await res.json();
        const blocks = data.blocks || [];

        // Extract reader_shell settings
        const shellBlock = blocks.find(b => b.type === 'reader_shell');
        if (shellBlock?.props) {
          setShellSettings(prev => ({ ...prev, ...shellBlock.props }));
        }

        // Extract reader_footer settings
        const footerBlock = blocks.find(b => b.type === 'reader_footer');
        if (footerBlock?.props) {
          setFooterSettings(prev => ({ ...prev, ...footerBlock.props }));
        }

        console.info('[ReaderPageRenderer] Loaded reader config from Page Builder:', {
          shellSettings: shellBlock?.props,
          footerSettings: footerBlock?.props,
          blockCount: blocks.length,
          version: data.status,
        });
      } catch (err) {
        console.warn('[ReaderPageRenderer] Config fetch error, using defaults:', err.message);
      } finally {
        setConfigLoaded(true);
      }
    };

    fetchConfig();
  }, [version]);

  return { shellSettings, footerSettings, configLoaded };
};

/**
 * Content wrapper that uses the appropriate data hook
 */
const ReaderContent = ({ contentType, contentId, unitId, mode, options, isAdmin, shellSettings, footerSettings }) => {
  const comicData = useComicEpisodeContent(
    contentType === 'comic' ? contentId : null,
    contentType === 'comic' ? unitId : null,
    options
  );
  
  const novelData = useNovelChapterContent(
    contentType === 'novel' ? contentId : null,
    contentType === 'novel' ? unitId : null,
    options
  );

  const { content, metadata, loading, error } = contentType === 'comic' ? comicData : novelData;

  return (
    <ReaderContextProvider
      contentType={contentType}
      contentId={contentId}
      unitId={unitId}
      initialMode={mode}
      shellSettings={shellSettings}
    >
      <ReaderContentInner 
        content={content}
        metadata={metadata}
        loading={loading}
        error={error}
        isAdmin={isAdmin}
        shellSettings={shellSettings}
        footerSettings={footerSettings}
      />
    </ReaderContextProvider>
  );
};

/**
 * Inner content component that has access to context
 */
const ReaderContentInner = ({ content, metadata, loading, error, isAdmin, shellSettings, footerSettings }) => {
  const ctx = useReaderContext();

  useEffect(() => {
    ctx.setContent(content);
    ctx.setMetadata(metadata);
    ctx.setIsLoading(loading);
    ctx.setError(error);
  }, [content, metadata, loading, error]);

  const ModeComponent = MODE_COMPONENTS[ctx.mode] || VerticalMode;

  return (
    <ReaderShell isAdmin={isAdmin} shellSettings={shellSettings} footerSettings={footerSettings}>
      <ModeComponent />
    </ReaderShell>
  );
};

/**
 * ReaderPageRenderer - Main entry point
 */
const ReaderPageRenderer = () => {
  const { contentType, contentId, unitId } = useParams();
  const [searchParams] = useSearchParams();
  const { legacyEnabled, isAdmin } = useLegacyRendererDebug();

  const mode = useMemo(() => parseModeFromQuery(searchParams), [searchParams]);
  const version = searchParams.get('version');
  const options = useMemo(() => ({ version: version === 'draft' ? 'draft' : 'published' }), [version]);

  // Fetch reader Page Builder config
  const { shellSettings, footerSettings, configLoaded } = useReaderPageConfig(
    version === 'draft' ? 'draft' : 'published'
  );

  // ========================================================================
  // CANONICAL RENDERING LOG - REQUIRED FOR VERIFICATION
  // ========================================================================
  useEffect(() => {
    if (configLoaded) {
      console.info('[ReaderPageRenderer] Canonical reader render via Page Builder:', {
        contentType,
        contentId,
        unitId,
        mode,
        shellSettings,
        configLoaded: true,
        pipeline: 'CanonicalRouteProvider → registry → PageBuilderRenderer → ReaderPageRenderer',
      });
    }
  }, [contentType, contentId, unitId, mode, configLoaded, shellSettings]);

  // Validate content type
  if (!contentType || !['comic', 'novel'].includes(contentType)) {
    return <ReaderInvalidParams message={`Invalid content type: "${contentType}". Must be "comic" or "novel".`} />;
  }

  // Validate IDs
  if (!contentId || !unitId) {
    return <ReaderInvalidParams message="Missing content ID or unit ID in URL." />;
  }

  // Admin debug: legacy renderer toggle
  if (legacyEnabled) {
    return (
      <>
        <div className="min-h-screen bg-black flex items-center justify-center" data-testid="reader-legacy-mode">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Legacy Mode</h1>
            <p className="text-zinc-400 mb-4">Legacy reader not implemented.</p>
            <p className="text-zinc-500 text-sm mb-4">This toggle is for debugging only.</p>
          </div>
        </div>
        <AdminDebugPill />
      </>
    );
  }

  // Draft version only for admins
  if (version === 'draft' && !isAdmin) {
    return <ReaderError message="Draft preview is only available for administrators." />;
  }

  // Wait for config before rendering
  if (!configLoaded) {
    return <ReaderLoading />;
  }

  return (
    <div data-testid="reader-page-renderer" data-content-type={contentType} data-config-loaded="true">
      <AdminDebugPill />
      
      <ReaderContent
        contentType={contentType}
        contentId={contentId}
        unitId={unitId}
        mode={mode}
        options={options}
        isAdmin={isAdmin}
        shellSettings={shellSettings}
        footerSettings={footerSettings}
      />
    </div>
  );
};

export default ReaderPageRenderer;
